import {FC, useEffect} from 'react'
import {Redirect} from 'react-router-dom'
import {gql} from '@apollo/client'
import {LoadingPage} from 'components/ui/LoadingPage'
import {useMutation} from 'hooks/graphql'
import {useNotify} from 'hooks/useNotify'
import {FlowMode} from '../types'

const ACCEPT_PROCEDURE_STATUS = gql`
  mutation acceptProcedureStatus($input: AcceptProcedureStatusInput!) {
    acceptProcedureStatus(input: $input) {
      procedureStatus {
        id
      }
      errors
    }
  }
`

interface AcceptProcedureStatusQueryVariables {
  input: {
    procedureStatusId: string
  }
}

interface AcceptProcedureStatusQueryResult {
  acceptProcedureStatus: {
    procedureStatus?: {
      id: string
    }
    errors?: unknown
  }
}

const START_PROCEDURE_PROGRESS = gql`
  mutation startProcedureProgress($input: StartProcedureProgressInput!) {
    startProcedureProgress(input: $input) {
      procedureStatus {
        id
        employee {
          id
        }
        procedureChildcareDatum {
          id
          procedureChildcareChildDatum {
            id
            addSupport
          }
        }
      }
    }
  }
`

interface StartProcedureProgressQueryVariables {
  input: {
    procedureStatusId: string
  }
}

interface StartProcedureProgressQueryResult {
  startProcedureProgress: {
    procedureStatus: {
      id: string
      employee: {
        id: string
      }
      procedureChildcareDatum?: {
        id: string
        procedureChildcareChildDatum?: {
          id: string
          addSupport: boolean
        }
      }
    }
  }
}

interface StartProgressAndRedirectProps {
  flowMode: FlowMode
  procedureStatusId: string
}

export const StartProgressAndRedirect: FC<StartProgressAndRedirectProps> = ({flowMode, procedureStatusId}) => {
  const notify = useNotify()
  const [acceptProcedureStatus] = useMutation<AcceptProcedureStatusQueryResult, AcceptProcedureStatusQueryVariables>(
    ACCEPT_PROCEDURE_STATUS
  )
  const [startProcedureProgress, {loading, data}] = useMutation<
    StartProcedureProgressQueryResult,
    StartProcedureProgressQueryVariables
  >(START_PROCEDURE_PROGRESS)

  useEffect(() => {
    ;(async () => {
      if (flowMode === 'confirm') {
        const {data} = await acceptProcedureStatus({variables: {input: {procedureStatusId}}})
        if (!data?.acceptProcedureStatus.procedureStatus) {
          console.error('failed to accept procedure', data?.acceptProcedureStatus.errors)
          notify('承認中にエラーが発生しました', 'error')
          return
        }
      }
      await startProcedureProgress({variables: {input: {procedureStatusId}}})
    })()
  }, [flowMode, procedureStatusId, notify, acceptProcedureStatus, startProcedureProgress])

  if (!loading && data) {
    const procedureStatus = data.startProcedureProgress.procedureStatus
    if (flowMode === 'draft' && procedureStatus.procedureChildcareDatum?.procedureChildcareChildDatum?.addSupport) {
      return (
        <Redirect
          to={`/add_dependents/detail?employee_id=${procedureStatus.employee.id}&childcare_procedure_status_id=${procedureStatusId}`}
        />
      )
    } else {
      return <Redirect to={`/procedure_statuses/${procedureStatusId}`} />
    }
  }

  return <LoadingPage />
}
